<template>
  <component
    :is="component"
    class="ui-text-button"
    :class="[
      `ui-text-button--${variant}`,
      {
        'is-active': active,
        'is-disabled': disabled,
        'is-group-hover-enabled': groupHover
      }
    ]"
    v-bind="componentAttributes"
    v-on="$listeners"
    @dragstart.prevent
  >
    <span v-if="hasLeadingIcon" class="ui-text-button__icon">
      <slot name="icon">
        <img
          v-if="variant === 'text-icon'"
          src="@/assets/img/ui/text-icon.svg"
          alt=""
          aria-hidden="true"
          draggable="false"
        >
        <span v-else class="ui-text-button__checkbox" aria-hidden="true">
          <svg viewBox="0 0 9 7" fill="none">
            <path d="M1 3.5L3.25 5.75L8 1" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </slot>
    </span>

    <span class="ui-text-button__label"><slot /></span>

    <span v-if="hasTrailingIcon" class="ui-text-button__icon">
      <slot name="icon">
        <img
          src="@/assets/img/ui/text-static-icon.svg"
          alt=""
          aria-hidden="true"
          draggable="false"
        >
      </slot>
    </span>
  </component>
</template>

<script>
const VARIANTS = [
  'text-menu',
  'text-large',
  'text-medium',
  'text-small',
  'text-static',
  'text-static-icon',
  'text-icon',
  'text-checkbox'
]

export default {
  name: 'UiTextButton',
  inheritAttrs: false,

  props: {
    variant: {
      type: String,
      default: 'text-small',
      validator: value => VARIANTS.includes(value)
    },
    tag: {
      type: String,
      default: ''
    },
    to: {
      type: [String, Object],
      default: ''
    },
    href: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'button'
    },
    active: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    groupHover: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    component () {
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      return this.tag || 'button'
    },

    componentAttributes () {
      const attributes = { ...this.$attrs }

      if (this.to) attributes.to = this.to
      if (this.href) attributes.href = this.href
      if (this.to || this.href) attributes.draggable = 'false'
      if (!this.to && !this.href && !this.tag) attributes.type = this.type
      if (this.active && this.variant !== 'text-checkbox') {
        attributes['aria-current'] = attributes['aria-current'] || 'page'
      }
      if (this.variant === 'text-checkbox') attributes['aria-pressed'] = String(this.active)

      if (this.disabled) {
        attributes['aria-disabled'] = 'true'
        attributes.tabindex = -1
        if (!this.to && !this.href && !this.tag) attributes.disabled = true
      }

      return attributes
    },

    hasLeadingIcon () {
      return ['text-icon', 'text-checkbox'].includes(this.variant)
    },

    hasTrailingIcon () {
      return this.variant === 'text-static-icon'
    }
  }
}
</script>

<style lang="scss">
@import './tokens';

.ui-text-button {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0;
  border: 0;
  background: transparent;
  color: $ui-color-text;
  font-family: "Helvetica", Arial, sans-serif;
  font-style: normal;
  text-align: left;
  user-select: none;
  -webkit-user-drag: none;
  cursor: pointer;
  appearance: none;

  &__label {
    position: relative;
    display: inline-block;
  }

  &__label::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: vw(-1);
    left: 0;
    height: vw(1);
    background: currentColor;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.3s ease;
  }

  &--text-menu,
  &--text-large {
    gap: vw(10);
    font-size: vw(32);
    font-weight: 300;
    line-height: 1.2;
    text-transform: uppercase;
  }

  &--text-menu {
    color: $ui-color-muted;
    transition: color 0.3s ease;

    &:hover,
    &:active,
    &.is-active,
    &.router-link-active {
      color: $ui-color-text;
    }
  }

  &--text-medium {
    gap: vw(10);
    font-size: vw(24);
    font-weight: 300;
    line-height: 1.2;
    text-transform: uppercase;
  }

  &--text-small,
  &--text-static,
  &--text-static-icon {
    gap: vw(10);
    font-size: vw(14);
    font-weight: 400;
    line-height: 1.3;
  }

  &--text-small {
    text-transform: uppercase;
  }

  &--text-icon,
  &--text-checkbox {
    gap: vw(8);
    font-size: vw(12);
    font-weight: 400;
    line-height: 1.3;
  }

  &__icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
  }

  &--text-static-icon &__icon {
    width: vw(15);
    height: vw(14);
  }

  &--text-static-icon &__icon img,
  &--text-icon &__icon img {
    display: block;
    width: 100%;
    height: 100%;
  }

  &--text-icon &__icon {
    width: vw(16);
    height: vw(17);
  }

  &--text-checkbox &__icon {
    width: vw(18);
    height: vw(18);
  }

  &__checkbox {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: vw(14);
    height: vw(14);
    border: vw(1) solid currentColor;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  &__checkbox svg {
    display: block;
    width: vw(8);
    height: vw(6);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &--text-checkbox:not(.is-active):not(.router-link-active):hover &__checkbox {
    background: $ui-color-muted;
  }

  &--text-checkbox.is-active &__checkbox svg,
  &--text-checkbox.router-link-active &__checkbox svg {
    opacity: 1;
  }

  &--text-large:hover &__label::after,
  &--text-large:active &__label::after,
  &--text-large.is-active &__label::after,
  &--text-large.router-link-active &__label::after,
  &--text-medium:hover &__label::after,
  &--text-medium:active &__label::after,
  &--text-medium.is-active &__label::after,
  &--text-medium.router-link-active &__label::after,
  &--text-small:hover &__label::after,
  &--text-small:active &__label::after,
  &--text-small.is-active &__label::after,
  &--text-small.router-link-active &__label::after,
  &--text-menu:active &__label::after,
  &--text-menu.is-active &__label::after,
  &--text-menu.router-link-active &__label::after {
    transform: scaleX(1);
  }

  &.is-disabled,
  &:disabled {
    pointer-events: none;
    cursor: default;
    opacity: 0.45;
  }
}

.ui-hover-group:hover,
.ui-hover-group:active,
.ui-hover-group.is-active {
  .ui-text-button.is-group-hover-enabled {
    &.ui-text-button--text-menu {
      color: $ui-color-text;
    }

    &.ui-text-button--text-large,
    &.ui-text-button--text-medium,
    &.ui-text-button--text-small {
      .ui-text-button__label::after {
        transform: scaleX(1);
      }
    }
  }
}

@include max-width(mobile) {
  .ui-text-button {
    &__label::after {
      bottom: vwm(-1);
      height: vwm(1);
    }

    &--text-small &__label::after {
      transform: scaleX(1);
    }

    &--text-menu,
    &--text-large {
      gap: vwm(10);
      font-size: vwm(24);
    }

    &--text-menu {
      color: $ui-color-text;
    }

    &--text-medium {
      gap: vwm(10);
      font-size: vwm(18);
    }

    &--text-small,
    &--text-static,
    &--text-static-icon {
      gap: vwm(10);
      font-size: vwm(12);
    }

    &--text-icon,
    &--text-checkbox {
      gap: vwm(10);
      font-size: vwm(10);
    }

    &--text-static-icon &__icon {
      width: vwm(13);
      height: vwm(8);
    }

    &--text-icon &__icon {
      width: vwm(16);
      height: vwm(17);
    }

    &--text-checkbox &__icon {
      width: vwm(18);
      height: vwm(18);
    }

    &__checkbox {
      width: vwm(14);
      height: vwm(14);
      border-width: vwm(1);
    }

    &__checkbox svg {
      width: vwm(8);
      height: vwm(6);
    }
  }
}
</style>
